import { createContext, forwardRef, useContext, useRef } from "react";
import { defineMessages } from "react-intl";
import { Carousel as NukaCarousel } from "nuka-carousel";

import { useFormatMessage } from "../Intl/Context";
import { useUniqueId } from "../Utils/uniqueId";
import { assignForwardedRef } from "../Utils/assignForwardedRef";
import { useDeferredEffect } from "../Utils/useDeferredEffect";
import AnnounceSlide from "./AnnounceSlide";

const CarouselContext = createContext();

export const useCarouselContext = () => useContext(CarouselContext);

const Carousel = (props, ref) => {
  const {
    children,
    autoplay: originalAutoplay,
    autoplayInterval,
    ...passthroughProps
  } = props;

  const t = useFormatMessage();
  const ariaLabel = t(intlMessages.ariaLabel);

  const carouselRef = useRef();
  const carouselFrameGeneratedDOMId = useUniqueId("carousel-frame-");
  const carouselFrameDOMId = props.carouselFrameId || carouselFrameGeneratedDOMId;

  // Ensure that we don't have `autoplay` set to true but `autoplayInterval` empty or set
  // to 0, which makes the carousel rapidly loop between images and is unreadable for the
  // user.
  const autoplay = originalAutoplay && autoplayInterval && autoplayInterval > 0;

  const setCarouselApi = useDeferredEffect(() => {
    const element = document.getElementById(carouselFrameDOMId);

    if (!element) return;

    // Since nuka-carousel provides the carousel frame container as it's innerRef, we
    // fetch it's parent element which is the carousel root container and use it as our
    // carouselRef
    carouselRef.current = element.parentElement;
    // If a ref was forwarded from the parent component, assign it the carousel frame
    // element
    assignForwardedRef(ref, element);
  }, [ref, carouselFrameDOMId]);

  return (
    <CarouselContext.Provider value={{ carouselFrameDOMId }}>
      <NukaCarousel
        title={ariaLabel}
        autoplay={autoplay}
        autoplayInterval={autoplayInterval}
        keyboard
        wrapMode="wrap"
        ref={setCarouselApi}
        id={carouselFrameDOMId}
        {...passthroughProps}
      >
        {children}

        {/* AnnounceSlide is used to announce the current slide to screen readers. It's a
            feature that has been removed from Nuka Carousel but was asked for the RGAA
            compliance, so we implemented it ourselves. */}
        <AnnounceSlide ariaLive={autoplay ? "off" : "polite"} />
      </NukaCarousel>
    </CarouselContext.Provider>
  );
};

export default forwardRef(Carousel);

const intlMessages = defineMessages({
  ariaLabel: {
    id: "carousel.ariaLabel",
    defaultMessage: "Diaporama"
  }
});
